import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { snoozeReminder, dismissAlertModal } from '../../Redux/Slice/ReminderSlice';
import { toast } from 'react-toastify';
import { Clock, Calendar, Check, X, AlertCircle } from 'lucide-react';
import moment from 'moment';

const SnoozeModal = ({ reminder, show, onHide, onSuccess }) => {
  const dispatch = useDispatch();
  const { LoginData } = useSelector((state) => state.auth);
  const currentUserId = LoginData?.data?._id;

  const [selectedOption, setSelectedOption] = useState(10); // default 10 mins
  const [isCustom, setIsCustom] = useState(false);
  const [customDate, setCustomDate] = useState(
    moment().format('YYYY-MM-DD')
  );
  const [customTime, setCustomTime] = useState(
    moment().add(15, 'minutes').format('HH:mm')
  );
  const [loading, setLoading] = useState(false);

  if (!show || !reminder) return null;

  const presets = [
    { label: '5 Minutes', value: 5 },
    { label: '10 Minutes', value: 10 },
    { label: '30 Minutes', value: 30 },
    { label: '1 Hour', value: 60 },
  ];

  const handleSnooze = async () => {
    setLoading(true);
    try {
      let payload = {
        id: reminder._id,
        userId: currentUserId,
      };

      if (isCustom) {
        if (!customDate || !customTime) {
          toast.error('Please select both date and time for custom snooze.');
          setLoading(false);
          return;
        }

        const selectedDateTime = moment(
          `${customDate} ${customTime}`,
          'YYYY-MM-DD HH:mm'
        );

        if (!selectedDateTime.isAfter(moment())) {
          toast.error('Snooze time must be in the future.');
          setLoading(false);
          return;
        }

        payload.customDate = customDate;
        payload.customTime = customTime;
      } else {
        payload.minutes = selectedOption;
      }

      await dispatch(snoozeReminder(payload)).unwrap();
      dispatch(dismissAlertModal(reminder._id));
      toast.success(
        isCustom
          ? `Reminder snoozed until ${moment(`${customDate} ${customTime}`, 'YYYY-MM-DD HH:mm').format('DD MMM, hh:mm A')}`
          : `Reminder snoozed for ${selectedOption} minutes`
      );

      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      toast.error(err || 'Failed to snooze reminder.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(8px)',
        zIndex: 1080,
      }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px' }}>
        <div className="modal-content border-0 shadow-2xl rounded-4 overflow-hidden">
          {/* Header */}
          <div
            className="modal-header border-0 px-4 pt-4 pb-3"
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              color: '#ffffff',
            }}
          >
            <div className="d-flex align-items-center gap-2.5">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: '38px',
                  height: '38px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  boxShadow: '0 4px 10px rgba(245, 158, 11, 0.35)',
                }}
              >
                <Clock size={20} className="text-white" />
              </div>
              <div>
                <h6 className="modal-title fw-bold mb-0 text-white" style={{ fontSize: '15.5px' }}>
                  Snooze Reminder
                </h6>
                <small className="text-white-50" style={{ fontSize: '11.5px' }}>
                  Choose when you want to be reminded again
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onHide}
              disabled={loading}
              aria-label="Close"
            />
          </div>

          {/* Body */}
          <div className="modal-body px-4 py-3">
            <div className="p-3 mb-3 rounded-3 bg-light border">
              <div className="fw-semibold text-dark text-truncate mb-1" style={{ fontSize: '13px' }}>
                {reminder.title}
              </div>
              {reminder.callNo && (
                <span className="badge bg-primary bg-opacity-10 text-primary fw-medium" style={{ fontSize: '11px' }}>
                  Token #{reminder.callNo}
                </span>
              )}
            </div>

            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '12px' }}>
              Quick Snooze Options
            </label>
            <div className="row g-2 mb-3">
              {presets.map((p) => {
                const isSelected = !isCustom && selectedOption === p.value;
                return (
                  <div key={p.value} className="col-6">
                    <button
                      type="button"
                      className={`btn w-100 py-2 d-flex align-items-center justify-content-center gap-2 rounded-3 border transition-all ${
                        isSelected
                          ? 'btn-primary text-white shadow-sm fw-semibold'
                          : 'btn-outline-light text-dark bg-white border-secondary border-opacity-25'
                      }`}
                      style={{ fontSize: '12.5px' }}
                      onClick={() => {
                        setIsCustom(false);
                        setSelectedOption(p.value);
                      }}
                    >
                      <Clock size={14} />
                      {p.label}
                      {isSelected && <Check size={14} className="ms-auto" />}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Custom Option Toggle */}
            <div className="border-top pt-3">
              <button
                type="button"
                className={`btn w-100 py-2 d-flex align-items-center justify-content-center gap-2 rounded-3 border mb-3 ${
                  isCustom
                    ? 'btn-primary text-white shadow-sm fw-semibold'
                    : 'btn-outline-light text-dark bg-white border-secondary border-opacity-25'
                }`}
                style={{ fontSize: '12.5px' }}
                onClick={() => setIsCustom(true)}
              >
                <Calendar size={14} />
                Custom Date & Time
                {isCustom && <Check size={14} className="ms-auto" />}
              </button>

              {isCustom && (
                <div className="p-3 bg-light rounded-3 border animate__animated animate__fadeIn">
                  <div className="mb-2">
                    <label className="form-label text-secondary mb-1" style={{ fontSize: '11px', fontWeight: '600' }}>
                      Date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customDate}
                      min={moment().format('YYYY-MM-DD')}
                      onChange={(e) => setCustomDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label text-secondary mb-1" style={{ fontSize: '11px', fontWeight: '600' }}>
                      Time
                    </label>
                    <input
                      type="time"
                      className="form-control form-control-sm"
                      value={customTime}
                      onChange={(e) => setCustomTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 px-4 pb-4 pt-1 d-flex gap-2">
            <button
              type="button"
              className="btn btn-light rounded-3 px-3 py-2 text-secondary fw-semibold border"
              style={{ fontSize: '13px' }}
              onClick={onHide}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary rounded-3 px-4 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
              style={{ fontSize: '13px' }}
              onClick={handleSnooze}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Snoozing...
                </>
              ) : (
                <>
                  <Clock size={15} />
                  Confirm Snooze
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnoozeModal;
